/**
 * useCredits Hook - Manages user credit balance and operations
 * Provides credit management functionality for authenticated users
 */

import { useState, useEffect, useCallback } from 'react';
import { creditsAPI } from '../services/api/CreditsAPI';
import type { CreditOperation } from '../types/credits';

/**
 * Return type for useCredits hook
 */
export interface UseCreditsReturn {
  /** Current credit balance */
  credits: number;
  /** Whether credits are being loaded */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether user has credits */
  hasCredits: boolean;
  /** Fetch current credit balance */
  fetchCredits: () => Promise<void>;
  /** Use a credit for an operation */
  useCredit: (operation?: CreditOperation) => Promise<boolean>;
  /** Refresh credit balance */
  refreshCredits: () => Promise<void>;
  /** Initiate credit purchase */
  purchaseCredits: (packageId: string) => Promise<void>;
  /** Check if user has sufficient credits */
  checkCredits: (required?: number) => boolean;
}

/**
 * Props for useCredits hook
 */
export interface UseCreditsProps {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether to auto-fetch credits on mount */
  autoFetch?: boolean;
}

/**
 * Custom hook for managing user credits
 * 
 * @param props - Hook configuration
 * @returns Credit state and control methods
 */
export function useCredits({
  isAuthenticated,
  autoFetch = true,
}: UseCreditsProps): UseCreditsReturn {
  const [credits, setCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch credit balance from API
   */
  const fetchCredits = useCallback(async () => {
    if (!isAuthenticated) {
      setCredits(0);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const balance = await creditsAPI.getCredits();
      setCredits(balance);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credits';
      console.error('[useCredits] Failed to fetch credits', err);
      setError(errorMessage);
      setCredits(0);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Use a credit for an operation
   * 
   * @param operation - Type of operation
   * @returns True if successful
   */
  const useCredit = useCallback(
    async (operation: CreditOperation = 'generate'): Promise<boolean> => {
      if (!isAuthenticated) {
        setError('Not authenticated');
        return false;
      }

      if (credits <= 0) {
        setError('Insufficient credits');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const remainingCredits = await creditsAPI.useCredit(operation);
        setCredits(remainingCredits);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to use credit';
        console.error('[useCredits] Failed to use credit', err);
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, credits]
  );

  /**
   * Refresh credit balance
   */
  const refreshCredits = useCallback(async () => {
    await fetchCredits();
  }, [fetchCredits]);

  /**
   * Initiate credit purchase
   * Redirects to Stripe checkout
   * 
   * @param packageId - Credit package ID
   */
  const purchaseCredits = useCallback(
    async (packageId: string) => {
      if (!isAuthenticated) {
        setError('Not authenticated');
        return;
      }

      if (!packageId) {
        setError('Package ID is required');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await creditsAPI.purchaseCredits(packageId);
        
        // Redirect to Stripe checkout
        if (response.checkoutUrl) {
          window.location.href = response.checkoutUrl;
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initiate purchase';
        console.error('[useCredits] Failed to purchase credits', err);
        setError(errorMessage);
        setIsLoading(false);
      }
      // Don't set isLoading to false here as we're redirecting
    },
    [isAuthenticated]
  );

  /**
   * Check if user has sufficient credits
   * 
   * @param required - Number of credits required (default: 1)
   * @returns True if user has sufficient credits
   */
  const checkCredits = useCallback(
    (required: number = 1): boolean => {
      return credits >= required;
    },
    [credits]
  );

  /**
   * Fetch credits on mount if authenticated and autoFetch is enabled
   */
  useEffect(() => {
    if (isAuthenticated && autoFetch) {
      fetchCredits();
    }
  }, [isAuthenticated, autoFetch, fetchCredits]);

  /**
   * Reset credits when user logs out
   */
  useEffect(() => {
    if (!isAuthenticated) {
      setCredits(0);
      setError(null);
    }
  }, [isAuthenticated]);

  return {
    credits,
    isLoading,
    error,
    hasCredits: credits > 0,
    fetchCredits,
    useCredit,
    refreshCredits,
    purchaseCredits,
    checkCredits,
  };
}
